import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { StyleLibrary } from '@vm/omega-shared-component-webapp';
import { ProductListItem, ComponentStyle, TileTag } from '../../types/types';

import { TilesBackgroundPattern } from './TilesBackgroundPattern';
import { TilesHeader } from './TilesHeader';
import TilesTags from './TilesTags';
import TileDetail from './TileDetail';
import TileSet from './TileSet';
import { filterTiles } from './utils';

const { pxToRem, mqBreakpoints } = StyleLibrary;

const TilesContainer = styled.div<{ hasBackgroundPattern: boolean }>(props => ({
  backgroundColor: props.hasBackgroundPattern ? 'var(--color-dark)' : 'transparent',
  color: props.hasBackgroundPattern ? 'var(--color-white)' : 'var(--color-body)',
  overflow: 'hidden',
  position: 'relative',
  padding: `${pxToRem(64)} ${pxToRem(32)} ${pxToRem(48)} ${pxToRem(32)}`,
  // z-index is 1 here to ensure the pattern is displayed
  zIndex: 1,

  [mqBreakpoints.tablet]: {
    padding: `${pxToRem(80)} ${pxToRem(48)} ${pxToRem(48)} ${pxToRem(48)}`,
  },

  [mqBreakpoints.desktop]: {
    padding: `${pxToRem(80)} ${pxToRem(160)}`,
  },
}));

type Props = {
  id: string;
  title: string;
  description?: string;
  style: ComponentStyle;
  tileItems: ProductListItem[];
  backgroundPattern?: boolean;
  handleProductClick: (id: string) => void;
};

export const Tiles: React.FC<Props> = ({ id, style, title, description, tileItems, backgroundPattern, handleProductClick }) => {
  const [productIndex, setProductIndex] = useState(0); // 0 initially
  const [filter, setProductFilter] = useState(TileTag.All);

  const setProduct = (i: number) => {
    setProductIndex(i);
  };
  const setFilter = (filter: TileTag) => setProductFilter(filter);

  const filteredTiles = filter === TileTag.All ? tileItems : filterTiles(tileItems, filter);

  useEffect(() => {
    setProductIndex(0);
  }, [filter]);

  const tileItem = filteredTiles[productIndex] ?? null;
  const productId = tileItem?.product.productId;

  return (
    <TilesContainer className={style} data-testid={`mp-tiles-container-${id}`} hasBackgroundPattern={backgroundPattern}>
      {backgroundPattern && <TilesBackgroundPattern />}
      <TilesHeader className={style} title={title} strapline={description} />
      <TilesTags tileItems={tileItems} filter={filter} setFilter={setFilter} style={style} />
      <TileSet tileItems={tileItems} style={style} filter={filter} selectedProductIndex={productIndex} handleTileClick={setProduct} />
      <TileDetail tileItem={tileItem} onCtaClick={() => handleProductClick(productId)} style={style} data-testid='mp-tiles-detail' />
    </TilesContainer>
  );
};

export default Tiles;
